import { PServed, useIM } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, Ref, ref, watch, WatchHandle } from 'vue'

import { useChatMessageStore } from '@/entities/messages'

import { useIMStore } from '@/shared/store/im'
import { pServedIsRoom } from '@/shared/utils/uri'

import { ExtendedInfoItem } from '../types'

let firstRun = true
const data = ref<Map<PServed, Array<ExtendedInfoItem>>>(new Map())
let unwatch: WatchHandle | undefined
const showExtendedInfo = ref(false)

export const useExtendedInfo = (pServed: Ref<PServed>) => {
  const { onInfoExtendedReceived, sendExtendedInfo, sendMessage } = useIM()
  const { addMessage } = useChatMessageStore()
  const { imIsReady } = storeToRefs(useIMStore())
  const extendedInfoData = computed(() => data.value.get(pServed.value))
  const extendedInfoListener = () => {
    if (pServedIsRoom(pServed.value) || !imIsReady.value) {
      return
    }
    void sendExtendedInfo({
      to: pServed.value,
      extendedParams: {
        type: 'ping',
      },
    }).catch(() => undefined)
  }

  const processExtendedInfo = async (item: ExtendedInfoItem) => {
    const response = await sendMessage({
      messageText: `/${item.slug}`,
      to: pServed.value,
    })
    addMessage(pServed.value, response)
  }

  const initExtendedInfo = (inputElement: Ref<HTMLElement>) => {
    if (firstRun) {
      firstRun = false
      onInfoExtendedReceived<Array<ExtendedInfoItem>>((message) => {
        if (message?.extendedParams?.length) {
          const commands = message.extendedParams
          data.value.set(message.pServed, commands)
        }
      })
    }
    unwatch?.()
    unwatch = watch([inputElement, pServed], (_value, _oldValue, onCleanup) => {
      if (!pServed.value) {
        return
      }
      const el = inputElement.value
      if (!el) {
        return
      }
      el.addEventListener('focus', extendedInfoListener)
      onCleanup(() => {
        el.removeEventListener('focus', extendedInfoListener)
      })
      if (el === document.activeElement) {
        extendedInfoListener()
        showExtendedInfo.value = true
      }
    })
  }

  const stopExtendedInfo = () => {
    unwatch?.()
  }

  return {
    extendedInfoData,
    processExtendedInfo,
    initExtendedInfo,
    stopExtendedInfo,
    showExtendedInfo,
  }
}
